import { useState, useEffect } from 'react';
import {
  X,
  FileText,
  User,
  Building,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  Archive,
  Send,
  Save,
  MessageSquare,
} from 'lucide-react';
import { Button } from '../../components/ui/Button.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { Textarea } from '../../components/ui/Textarea.jsx';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/Table.jsx';
import { formatCurrency, formatDate } from '../../lib/utils.js';
import { INQUIRY_STATUS_MAP } from './admin-presets.js';

export function InquiryDetailDrawer({
  isOpen,
  onClose,
  inquiry = null,
  onUpdateStatus,
  updating = false,
}) {
  const [internalNotes, setInternalNotes] = useState('');
  const [activeStatus, setActiveStatus] = useState('new');

  useEffect(() => {
    if (inquiry) {
      setInternalNotes(inquiry.notes || '');
      setActiveStatus(inquiry.status || 'new');
    }
  }, [inquiry, isOpen]);

  if (!isOpen || !inquiry) return null;

  const statusConfig = INQUIRY_STATUS_MAP[activeStatus] || INQUIRY_STATUS_MAP.new;
  const items = inquiry.items || [];
  const totalNetCents = inquiry.totalEstimatedCents || inquiry.total_estimated || 0;
  const hasExplicitTax = (inquiry.taxCents !== undefined && inquiry.taxCents !== null) ||
                         (inquiry.tax_cents !== undefined && inquiry.tax_cents !== null);
  const vatCents = hasExplicitTax ? (inquiry.taxCents ?? inquiry.tax_cents) : null;
  const grossCents = (vatCents !== null) ? (inquiry.grossCents ?? inquiry.gross_cents ?? (totalNetCents + vatCents)) : null;

  const handleStatusChange = (newStatus) => {
    setActiveStatus(newStatus);
    if (onUpdateStatus) {
      onUpdateStatus(inquiry.id, newStatus, internalNotes);
    }
  };

  const handleSaveNotes = () => {
    if (onUpdateStatus) {
      onUpdateStatus(inquiry.id, activeStatus, internalNotes);
    }
  };

  // Generate Mailto quote response with only verified / explicit terms
  const rawCustomerEmail = (inquiry.customerEmail || inquiry.customer_email || '');
  const customerEmail = typeof rawCustomerEmail === 'string' && rawCustomerEmail.trim().length > 0 ? rawCustomerEmail.trim() : null;
  const deliveryTerms = (inquiry.deliveryTerms || inquiry.delivery_terms || inquiry.deliveryTime || inquiry.delivery_time || '').trim();
  const paymentTerms = (inquiry.paymentTerms || inquiry.payment_terms || '').trim();

  const deliveryClause = deliveryTerms ? `Lieferzeit: ${deliveryTerms}.` : '';
  const paymentClause = paymentTerms ? `Zahlungsbedingungen: ${paymentTerms}.` : '';

  const taxSummaryLines = vatCents !== null
    ? `Gesamtsumme Netto: ${formatCurrency(totalNetCents)}\nzzgl. USt.: ${formatCurrency(vatCents)}\nGesamtsumme Brutto: ${formatCurrency(grossCents)}`
    : `Gesamtsumme Netto: ${formatCurrency(totalNetCents)}`;

  const itemSummaryText = items
    .map(
      (it, idx) =>
        `${idx + 1}. ${it.productName || it.product_name} (Art.-Nr. ${it.sku || it.productId}) - Menge: ${it.quantity} x ${it.unitPriceFormatted || formatCurrency(it.unitPriceCents || 0)} = ${it.totalPriceFormatted || formatCurrency(it.totalPriceCents || 0)}`
    )
    .join('\n');

  const emailSubject = encodeURIComponent(
    `B2B-Angebot zu Ihrer Anfrage ${inquiry.inquiryNumber || inquiry.id} (${inquiry.tenantName || 'Partner-Shop'})`
  );
  const clausesSection = [deliveryClause, paymentClause].filter(Boolean).join('\n');
  const emailSections = [
    `Sehr geehrte/r ${inquiry.customerName || 'Damen und Herren'},\n\nvielen Dank für Ihre Anfrage über unseren B2B Partner-Shop.\n\nGerne unterbreiten wir Ihnen folgendes unverbindliches Projektangebot:\n\n${itemSummaryText}\n\n${taxSummaryLines}`,
    clausesSection,
    `Für Rückfragen oder zur Freigabe des Angebots stehen wir Ihnen jederzeit gerne zur Verfügung.\n\nMit freundlichen Grüßen,\n${inquiry.tenantName || 'Ihr Partner-Team'}`
  ].filter(Boolean).join('\n\n');
  const emailBody = encodeURIComponent(emailSections);
  const mailtoHref = customerEmail ? `mailto:${customerEmail}?subject=${emailSubject}&body=${emailBody}` : null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-slate-900/50 backdrop-blur-xs">
      <div className="absolute inset-y-0 right-0 flex max-w-full pl-10">
        <div className="w-screen max-w-2xl bg-white shadow-2xl border-l border-[var(--border)] flex flex-col">
          {/* Drawer Header */}
          <div className="border-b border-[var(--border)] px-6 py-4 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--primary)] text-white">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-base font-bold text-slate-900">
                    {inquiry.inquiryNumber || inquiry.id}
                  </h2>
                  <Badge variant={statusConfig.badgeVariant} className="text-[10px]">
                    {statusConfig.label}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                  <span>Mandant: <strong>{inquiry.tenantName || inquiry.tenantSlug || inquiry.tenantId}</strong></span>
                  <span>•</span>
                  <span>{formatDate(inquiry.createdAt || inquiry.created_at)}</span>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700 focus:outline-none"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Drawer Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Customer Information Card */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--secondary)] flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                Kundendaten & Anfragedetails
              </h3>

              <div className="grid gap-3 sm:grid-cols-2 text-xs">
                <div>
                  <span className="text-slate-500 block">Kunde:</span>
                  <span className="font-semibold text-slate-900">{inquiry.customerName || '-'}</span>
                </div>

                <div>
                  <span className="text-slate-500 block">Unternehmen:</span>
                  <span className="font-semibold text-slate-900">{inquiry.customerCompany || '-'}</span>
                </div>

                <div>
                  <span className="text-slate-500 block">E-Mail:</span>
                  {customerEmail ? (
                    <a
                      href={`mailto:${customerEmail}`}
                      className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Mail className="h-3 w-3" />
                      {customerEmail}
                    </a>
                  ) : (
                    <span className="font-normal text-slate-400 italic">Keine Angabe</span>
                  )}
                </div>

                <div>
                  <span className="text-slate-500 block">Telefon:</span>
                  <span className="font-medium text-slate-800 flex items-center gap-1">
                    <Phone className="h-3 w-3 text-slate-400" />
                    {inquiry.customerPhone || '-'}
                  </span>
                </div>
              </div>

              {inquiry.notes && (
                <div className="pt-2 border-t border-slate-100 text-xs">
                  <span className="text-slate-500 font-semibold block mb-1">Kunden-Nachricht / Projekt:</span>
                  <div className="rounded-lg bg-slate-50 p-2.5 text-slate-700 whitespace-pre-wrap font-sans border border-slate-200/80">
                    {inquiry.notes}
                  </div>
                </div>
              )}
            </div>

            {/* Line Items Table */}
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-xs">
              <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Positionen ({items.length})
                </h3>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50">
                      <TableHead className="text-xs font-semibold">Artikel</TableHead>
                      <TableHead className="text-xs font-semibold text-center">Menge</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Einzelpreis Netto</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Gesamt Netto</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="h-20 text-center text-xs text-slate-400">
                          Keine Positionen übermittelt.
                        </TableCell>
                      </TableRow>
                    ) : (
                      items.map((item, idx) => (
                        <TableRow key={item.id || idx}>
                          <TableCell>
                            <div className="font-medium text-xs text-slate-900">
                              {item.productName || item.product_name || 'Produkt'}
                            </div>
                            <div className="text-[10px] font-mono text-slate-400">
                              SKU: {item.sku || item.productId || '-'}
                            </div>
                          </TableCell>
                          <TableCell className="text-center font-semibold text-xs text-slate-800">
                            {item.quantity}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs text-slate-600">
                            {item.unitPriceFormatted || formatCurrency(item.unitPriceCents || item.unit_price || 0)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs font-bold text-slate-900">
                            {item.totalPriceFormatted || formatCurrency(item.totalPriceCents || item.total_price || 0)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Total Calculation Summary */}
              <div className="bg-slate-50/80 p-4 border-t border-slate-200 space-y-1.5 text-xs">
                <div className="flex justify-between text-slate-600">
                  <span>Zwischensumme Netto:</span>
                  <span className="font-mono font-medium">{formatCurrency(totalNetCents)}</span>
                </div>
                {vatCents !== null ? (
                  <>
                    <div className="flex justify-between text-slate-600">
                      <span>zzgl. USt. (MwSt.):</span>
                      <span className="font-mono font-medium">{formatCurrency(vatCents)}</span>
                    </div>
                    <div className="flex justify-between text-slate-900 font-bold text-sm pt-2 border-t border-slate-200">
                      <span>Geschätzter Gesamtwert (Brutto):</span>
                      <span className="font-mono text-[var(--primary)]">{formatCurrency(grossCents)}</span>
                    </div>
                  </>
                ) : (
                  <div className="pt-2 border-t border-slate-200 text-slate-500 text-[11px] leading-tight">
                    <span>Preise verstehen sich rein netto (Steuer- und Frachtangaben nicht im Datensatz hinterlegt).</span>
                  </div>
                )}
              </div>
            </div>

            {/* Internal Processing Notes */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--secondary)] flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5" />
                  Interne Notizen & Bearbeitungsstatus
                </h3>
                <Button variant="outline" size="sm" onClick={handleSaveNotes} disabled={updating} className="h-7 text-xs">
                  <Save className="h-3 w-3 mr-1" />
                  Speichern
                </Button>
              </div>
              <Textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                placeholder="Interne Notizen zum Angebot oder Rückruf eintragen..."
                rows={3}
                className="text-xs"
              />
            </div>

            {/* Quick Actions / Quote Generator */}
            <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 space-y-3">
              <h3 className="text-xs font-bold text-blue-900 uppercase tracking-wider flex items-center gap-1.5">
                <Send className="h-3.5 w-3.5 text-blue-700" />
                Angebot versenden
              </h3>
              <p className="text-xs text-blue-800 leading-relaxed">
                Generieren Sie eine E-Mail-Antwort mit allen angefragten Artikeln und Preisen.
              </p>
              {customerEmail ? (
                <a
                  href={mailtoHref}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white shadow-xs hover:bg-blue-700 transition-colors"
                >
                  <Mail className="h-3.5 w-3.5" />
                  Angebot per Mail an {customerEmail} versenden
                </a>
              ) : (
                <p className="text-xs text-slate-500 italic">
                  Keine Kunden-E-Mail hinterlegt (Angebot per Mail nicht verfügbar).
                </p>
              )}
            </div>
          </div>

          {/* Drawer Footer: Status Transitions */}
          <div className="border-t border-[var(--border)] p-4 bg-slate-50 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-semibold text-slate-700 mr-1">Status setzen:</span>

              <Button
                variant={activeStatus === 'in_review' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleStatusChange('in_review')}
                disabled={updating}
                className="text-xs h-8"
              >
                In Prüfung
              </Button>

              <Button
                variant={activeStatus === 'quoted' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleStatusChange('quoted')}
                disabled={updating}
                className="text-xs h-8 text-blue-700 border-blue-300 hover:bg-blue-50"
              >
                Angebot erstellt
              </Button>

              <Button
                variant={activeStatus === 'accepted' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleStatusChange('accepted')}
                disabled={updating}
                className="text-xs h-8 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
              >
                Angenommen
              </Button>

              <Button
                variant={activeStatus === 'rejected' ? 'destructive' : 'outline'}
                size="sm"
                onClick={() => handleStatusChange('rejected')}
                disabled={updating}
                className="text-xs h-8 text-rose-700 border-rose-300 hover:bg-rose-50"
              >
                Ablehnen
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleStatusChange('archived')}
                disabled={updating}
                className="text-xs h-8 text-slate-500"
              >
                <Archive className="h-3.5 w-3.5 mr-1" />
                Archivieren
              </Button>
            </div>

            <Button variant="outline" size="sm" onClick={onClose} className="text-xs h-8">
              Schließen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
